import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, DfeLegacyImportResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseLegacyImportReturn {
  importResult: DfeLegacyImportResult | null;
  importing: boolean;
  error: string | null;
  performImport: (username: string, password: string) => Promise<void>;
  retryImport: (importId: string, password: string) => Promise<void>;
  reset: () => void;
}

export function useLegacyImport(): UseLegacyImportReturn {
  const queryClient = useQueryClient();
  const [importResult, setImportResult] = useState<DfeLegacyImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const importMutation = useMutation({
    mutationFn: (args: { username: string; password: string }) =>
      api.importDfeLegacyInventory(args),
    onSuccess: (result) => {
      setImportResult(result);
      setError(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryTransition.all });
    },
    onError: (err) => {
      setImportResult(null);
      setError(err instanceof Error ? err.message : 'Import failed');
    },
  });

  const retryMutation = useMutation({
    mutationFn: (args: { importId: string; password: string }) =>
      api.retryDfeLegacyImport(args.importId, args.password),
    onSuccess: (result) => {
      setImportResult(result);
      setError(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryTransition.all });
    },
    onError: (err) => {
      setImportResult(null);
      setError(err instanceof Error ? err.message : 'Retry failed');
    },
  });

  const performImport = useCallback(
    async (username: string, password: string) => {
      setImportResult(null);
      setError(null);
      await importMutation.mutateAsync({ username, password });
    },
    [importMutation],
  );

  const retryImport = useCallback(
    async (importId: string, password: string) => {
      setImportResult(null);
      setError(null);
      await retryMutation.mutateAsync({ importId, password });
    },
    [retryMutation],
  );

  const reset = useCallback(() => {
    setImportResult(null);
    setError(null);
    importMutation.reset();
    retryMutation.reset();
  }, [importMutation, retryMutation]);

  return {
    importResult,
    importing: importMutation.isPending || retryMutation.isPending,
    error,
    performImport,
    retryImport,
    reset,
  };
}
